import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { auth } from "@/api/endpoints";
import { Spinner } from "@/components/shared/Spinner";
import { useToast } from "@/components/shared/Toast";
import { useAuthStore } from "@/store/authStore";
import type { Role, User } from "@/types/api";

const ROLES: Role[] = ["viewer", "editor", "admin"];

export function UserManagement() {
  const showToast = useToast((s) => s.show);
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);

  const usersQ = useQuery({
    queryKey: ["auth", "users"],
    queryFn: () => auth.listUsers(),
  });

  const [creating, setCreating] = useState(false);
  const [resetFor, setResetFor] = useState<User | null>(null);

  const refetch = () => qc.invalidateQueries({ queryKey: ["auth", "users"] });

  const toggleActive = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      auth.updateUser(id, { is_active }),
    onSuccess: () => {
      refetch();
      showToast("User updated.", "success");
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "update failed", "error"),
  });

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => auth.updateUser(id, { role }),
    onSuccess: () => {
      refetch();
      showToast("Role updated.", "success");
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "update failed", "error"),
  });

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Users</h1>
        <button
          onClick={() => setCreating(true)}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm text-white hover:bg-brand-700"
        >
          New user
        </button>
      </div>

      {usersQ.isLoading ? (
        <Spinner />
      ) : usersQ.error ? (
        <div className="text-red-700">{(usersQ.error as Error).message}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
              <tr>
                <th className="px-3 py-2 text-left">Username</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Role</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Last login</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(usersQ.data ?? []).map((u) => {
                const isMe = me?.id === u.id;
                return (
                  <tr key={u.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2 font-medium">
                      {u.username}
                      {isMe && (
                        <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          you
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{u.email ?? "—"}</td>
                    <td className="px-3 py-2">
                      <select
                        value={u.role}
                        disabled={isMe || changeRole.isPending}
                        title={
                          isMe
                            ? "You can't change your own role from here — ask another admin"
                            : "Change role"
                        }
                        onChange={(e) =>
                          changeRole.mutate({ id: u.id, role: e.target.value as Role })
                        }
                        className="rounded-md border border-slate-300 px-2 py-1 text-sm dark:bg-slate-800 dark:border-slate-700 disabled:opacity-50"
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      {u.is_active ? (
                        <span className="text-emerald-700 dark:text-emerald-400">active</span>
                      ) : (
                        <span className="text-slate-500">disabled</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "never"}
                    </td>
                    <td className="px-3 py-2 text-right space-x-3">
                      <button
                        onClick={() => setResetFor(u)}
                        className="text-xs text-brand-700 hover:underline"
                      >
                        Reset password
                      </button>
                      <button
                        onClick={() =>
                          toggleActive.mutate({ id: u.id, is_active: !u.is_active })
                        }
                        disabled={isMe || toggleActive.isPending}
                        className="text-xs text-slate-600 hover:text-slate-900 disabled:opacity-30"
                        title={isMe ? "You can't disable your own account" : ""}
                      >
                        {u.is_active ? "Disable" : "Enable"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateUserDialog onClose={() => setCreating(false)} onCreated={refetch} />}
      {resetFor && (
        <ResetPasswordDialog
          user={resetFor}
          onClose={() => setResetFor(null)}
          onDone={refetch}
        />
      )}
    </section>
  );
}

function CreateUserDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("editor");
  const showToast = useToast((s) => s.show);

  const create = useMutation({
    mutationFn: () =>
      auth.createUser({
        username: username.trim(),
        email: email.trim() || undefined,
        password,
        role,
      }),
    onSuccess: (u) => {
      onCreated();
      onClose();
      showToast(`Created ${u.username}.`, "success");
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "create failed", "error"),
  });

  const valid =
    username.trim().length >= 3 && password.length >= 1 && ROLES.includes(role);

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 px-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) create.mutate();
        }}
        className="w-full max-w-sm space-y-3 rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900"
      >
        <h2 className="text-base font-semibold">New user</h2>
        <Field label="Username">
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
            required
            minLength={3}
            className="input"
          />
        </Field>
        <Field label="Email (optional)">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Initial password">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            className="input"
          />
          <span className="text-xs text-slate-500">
            Tell the user; they should change it after first login.
          </span>
        </Field>
        <Field label="Role">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="input"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || create.isPending}
            className="px-3 py-2 text-sm rounded-md bg-brand-500 text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {create.isPending ? "Creating…" : "Create"}
          </button>
        </div>
        <Inputs />
      </form>
    </div>
  );
}

function ResetPasswordDialog({
  user,
  onClose,
  onDone,
}: {
  user: User;
  onClose: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const showToast = useToast((s) => s.show);

  const reset = useMutation({
    mutationFn: () => auth.updateUser(user.id, { password }),
    onSuccess: () => {
      onDone();
      onClose();
      showToast(
        `Reset password for ${user.username}. Their other sessions are signed out.`,
        "success",
      );
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "reset failed", "error"),
  });

  const valid = password.length >= 1 && password === confirm;

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 px-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) reset.mutate();
        }}
        className="w-full max-w-sm space-y-3 rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900"
      >
        <h2 className="text-base font-semibold">Reset password for {user.username}</h2>
        <p className="text-xs text-slate-500">
          The user's existing sessions are revoked on save. Tell them the new password
          out-of-band — fleet-tracker doesn't email it.
        </p>
        <Field label="New password">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            autoFocus
            className="input"
          />
        </Field>
        <Field label="Confirm">
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            className="input"
          />
          {confirm && password !== confirm && (
            <span className="text-xs text-red-700">Passwords do not match.</span>
          )}
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || reset.isPending}
            className="px-3 py-2 text-sm rounded-md bg-brand-500 text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {reset.isPending ? "Saving…" : "Reset password"}
          </button>
        </div>
        <Inputs />
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="text-slate-600 dark:text-slate-300">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// Local input styling used by the dialogs in this file.
function Inputs() {
  return (
    <style>{`
      .input {
        width: 100%;
        border-radius: 0.375rem;
        border: 1px solid rgb(203 213 225);
        padding: 0.5rem 0.75rem;
        font-size: 0.875rem;
        background: white;
      }
      @media (prefers-color-scheme: dark) {
        .input { background: rgb(30 41 59); border-color: rgb(51 65 85); color: white; }
      }
    `}</style>
  );
}
